import Header from '../components/Header';
import Footer from '../components/Footer';
import { Shield, AlertCircle, Heart } from 'lucide-react';

const ResponsibleGaming = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <article className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center space-x-3 mb-6">
              <Shield className="text-yellow-accent" size={40} />
              <h1 className="text-4xl md:text-5xl font-bold text-gray-dark">
                Responsible Gaming
            </h1>
            </div>
            
            <div className="prose prose-lg max-w-none text-gray-light leading-relaxed space-y-6">
              <p className="text-xl text-gray-medium">
                At Sceptre Slots, we are committed to promoting responsible gambling and 
                protecting players from the potential harms associated with online gambling. 
                We believe that gambling should be entertaining and enjoyable, never a source 
                of financial hardship or personal distress. This page provides information, 
                resources, and tools to help you gamble responsibly.
              </p>

              <h2 className="text-3xl font-bold text-gray-dark mt-12 mb-6">Our Commitment</h2>
              
              <p>
                We are dedicated to providing a safe and responsible gambling environment. 
                While we review and recommend online casinos, we also recognize the importance 
                of promoting responsible gambling practices. We encourage all players to gamble 
                responsibly and seek help if gambling becomes problematic.
              </p>
              
              <p>
                Our commitment extends to providing accurate information about responsible 
                gambling tools, self-exclusion options, and support resources. We believe 
                that informed players make better decisions and are better equipped to 
                maintain control over their gambling activities.
              </p>

              <h2 className="text-3xl font-bold text-gray-dark mt-12 mb-6">What Is Responsible Gambling?</h2>
              
              <p>
                Responsible gambling means maintaining control over your gambling activities 
                and ensuring that gambling remains a form of entertainment rather than a 
                problem. It involves setting limits, understanding the risks, recognizing 
                warning signs, and knowing when to stop.
              </p>
              
              <p>
                Responsible gambling includes setting budgets before you play, never gambling 
                more than you can afford to lose, never chasing losses, and never gambling 
                when emotional or under the influence. It also means taking regular breaks 
                and maintaining balance between gambling and other activities.
              </p>
              
              <p>
                Gambling should enhance your life through entertainment, not create problems 
                through excess. If gambling stops being fun or starts causing stress, 
                financial difficulties, or relationship problems, it's time to reassess 
                your relationship with gambling.
              </p>

              <h2 className="text-3xl font-bold text-gray-dark mt-12 mb-6">Setting Limits</h2>
              
              <p>
                Setting limits is one of the most effective ways to maintain control over 
                your gambling. Before you start playing, decide how much money and time you 
                can afford to spend. Stick to these limits regardless of wins or losses.
              </p>
              
              <p>
                Most licensed UK casinos offer deposit limits, loss limits, and session 
                time limits that you can set in your account settings. These tools help 
                you maintain control by preventing you from exceeding predetermined amounts. 
                Use these tools proactively, not reactively.
              </p>
              
              <p>
                Remember that limits should be set when you're thinking clearly, not during 
                emotional moments or after losses. Set realistic limits that you can 
                comfortably afford, and review them regularly as your circumstances change.
              </p>

              <h2 className="text-3xl font-bold text-gray-dark mt-12 mb-6">Recognizing Problem Gambling</h2>
              
              <p>
                Problem gambling can develop gradually, making it important to recognize 
                warning signs early. Common indicators include spending more than you can 
                afford, chasing losses, lying about gambling, neglecting responsibilities, 
                feeling anxious when not gambling, or gambling to escape problems.
              </p>
              
              <p>
                Other warning signs include borrowing money to gamble, feeling unable to 
                stop, gambling until money runs out, or feeling guilty or anxious about 
                gambling. If you recognize these signs in yourself, it's important to take 
                action immediately.
              </p>
              
              <p>
                Friends and family often notice problem gambling before you do. If loved 
                ones express concern about your gambling, listen seriously. They see 
                changes you might miss, and their perspective can help you recognize 
                problems early.
              </p>

              <h2 className="text-3xl font-bold text-gray-dark mt-12 mb-6">Self-Exclusion Tools</h2>
              
              <p>
                Self-exclusion is a powerful tool that allows you to block access to gambling 
                sites for a specified period. All licensed UK casinos offer self-exclusion 
                options, allowing you to temporarily or permanently block your account.
              </p>
              
              <p>
                GamStop provides industry-wide self-exclusion across all UK licensed operators. 
                Registering with GamStop blocks access to every licensed casino for your chosen 
                period – 6 months, 1 year, or 5 years. This prevents switching between casinos 
                to continue gambling.
              </p>
              
              <p>
                Self-exclusion isn't failure – it's responsible action. Using these tools 
                demonstrates self-awareness and commitment to maintaining control. If you're 
                considering self-exclusion, you probably need it. Don't hesitate to use 
                these tools.
              </p>

              <h2 className="text-3xl font-bold text-gray-dark mt-12 mb-6">Getting Help</h2>
              
              <p>
                If gambling feels out of control, professional help is available. BeGambleAware 
                provides free, confidential support and resources for problem gambling. Their 
                website offers self-assessment tools, advice, and links to treatment services. 
                You can visit www.begambleaware.org or call their helpline for immediate support.
              </p>
              
              <p>
                GamCare offers similar support with trained advisors available via phone, live 
                chat, or email. They provide non-judgmental support and can help you develop 
                strategies for regaining control. These services are free, confidential, and 
                available 24/7.
              </p>
              
              <p>
                Support groups provide peer support from others who've experienced similar 
                challenges. Gamblers Anonymous offers meetings and online support for those 
                seeking recovery. Sharing experiences with others who understand can be 
                powerful and healing.
              </p>

              <h2 className="text-3xl font-bold text-gray-dark mt-12 mb-6">Resources and Support</h2>
              
              <div className="bg-gray-50 p-6 rounded-lg my-8">
                <h3 className="text-xl font-bold text-gray-dark mb-4">Organizations That Can Help</h3>
                <ul className="space-y-3 text-gray-light">
                  <li>
                    <strong className="text-gray-dark">BeGambleAware:</strong> www.begambleaware.org - 
                    Free support and resources for problem gambling
                  </li>
                  <li>
                    <strong className="text-gray-dark">GamCare:</strong> www.gamcare.org.uk - 
                    Support, advice, and treatment for problem gambling
                  </li>
                  <li>
                    <strong className="text-gray-dark">GamStop:</strong> www.gamstop.co.uk - 
                    Industry-wide self-exclusion service
                  </li>
                  <li>
                    <strong className="text-gray-dark">Gamblers Anonymous:</strong> www.gamblersanonymous.org.uk - 
                    Support groups and recovery programs
                  </li>
                  <li>
                    <strong className="text-gray-dark">Samaritans:</strong> 116 123 - 
                    Free, confidential emotional support available 24/7
                  </li>
                </ul>
              </div>

              <h2 className="text-3xl font-bold text-gray-dark mt-12 mb-6">Tips for Responsible Gambling</h2>
              
              <ul className="list-disc list-inside space-y-3 text-gray-light">
                <li>Set a budget before you play and stick to it</li>
                <li>Never gamble more than you can afford to lose</li>
                <li>Set time limits for gambling sessions</li>
                <li>Never chase losses by increasing bets</li>
                <li>Don't gamble when emotional, stressed, or under the influence</li>
                <li>Take regular breaks from gambling</li>
                <li>Don't gamble to solve financial problems</li>
                <li>Keep gambling in perspective – it's entertainment, not investment</li>
                <li>Use casino responsible gambling tools like deposit limits</li>
                <li>Seek help immediately if gambling becomes problematic</li>
              </ul>

              <h2 className="text-3xl font-bold text-gray-dark mt-12 mb-6">For Friends and Family</h2>
              
              <p>
                If you're concerned about someone else's gambling, approach conversations with 
                compassion and without judgment. Express concern about specific behaviors you've 
                noticed rather than making accusations. Offer support and information about 
                help resources without forcing action.
              </p>
              
              <p>
                You can't force someone to stop gambling, but you can encourage them to seek 
                help. Provide information about support services and be available to listen. 
                However, protect yourself financially – don't lend money or pay gambling debts, 
                as this enables continued problematic behavior.
              </p>
              
              <p>
                Support services are also available for friends and family affected by problem 
                gambling. GamAnon offers support groups for family members, helping you 
                understand problem gambling and develop strategies for supporting loved ones 
                while protecting yourself.
              </p>

              <h2 className="text-3xl font-bold text-gray-dark mt-12 mb-6">Our Role</h2>
              
              <p>
                As a casino review and comparison site, we recognize our responsibility to 
                promote responsible gambling. We provide information about responsible gambling 
                tools, support resources, and warning signs. We also ensure that the casinos 
                we recommend are licensed and offer proper responsible gambling tools.
              </p>
              
              <p>
                However, we cannot control how individuals use the information we provide. 
                Ultimately, responsible gambling is a personal responsibility. We encourage 
                all players to gamble responsibly, set limits, and seek help if needed.
              </p>

              <h2 className="text-3xl font-bold text-gray-dark mt-12 mb-6">Contact Us</h2>
              
              <p>
                If you have questions about responsible gambling or need information about 
                support resources, please contact us at:
              </p>
              
              <p className="font-semibold">
                Email: info@sceptreslots.com<br />
                Phone: +44 8147 675 497<br />
                Address: London, United Kingdom
              </p>
              
              <div className="bg-yellow-accent/10 border-l-4 border-yellow-accent p-6 my-8 rounded-r-lg">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="text-yellow-accent flex-shrink-0 mt-1" size={24} />
                  <div>
                    <p className="text-gray-light leading-relaxed">
                      <strong className="text-gray-dark">Remember:</strong> Gambling should be 
                      fun and entertaining. If it stops being enjoyable or starts causing problems, 
                      it's time to stop and seek help. Support is available, and recovery is 
                      possible. You don't have to face problem gambling alone.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default ResponsibleGaming;

